import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, TrendingUp, ArrowUpRight, Info, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import DataFreshnessStrip from '../components/DataFreshnessStrip';
import { api } from '../api';

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

function Stat({ value, label, accent }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
      <div className={`font-display text-2xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
    </div>
  );
}

function SpecBadge({ p }) {
  return (
    <div className="flex gap-1">
      {p.is_pm_r && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#E5F0EA] text-[#2D5F4F]">PM&R</span>}
      {p.is_internal_med && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#FBE6CC] text-[#A06628]">IM</span>}
      {p.is_app && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#E0EAF8] text-[#34538F]">NP/PA</span>}
    </div>
  );
}

export default function PhysicianIntelligencePage() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.physicianDashboard().then(setD).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Physician Intelligence' }]} />
        <div className="mt-3"><DataFreshnessStrip /></div>

        <header className="mt-6 max-w-3xl">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
            <Stethoscope size={12} className="text-[#4A7C5E]"/>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">Physician Alignment & Referral Intelligence</span>
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight leading-tight">
            Who's actually rounding in SNFs?
          </h1>
          <p className="mt-3 text-[14px] text-[#3A4A44] leading-relaxed">
            Every physician with a public Medicare Part B claim for SNF E&M codes, aligned to nearby facilities using
            geographic proximity + claims volume. PM&R, internal medicine, and APP coverage gaps surfaced per market.
            <strong className="text-[#1A2622]"> No patient-level data — all linkages are public-claims estimates.</strong>
          </p>
        </header>

        {loading && <div className="mt-8 text-sm text-[#7A857F]">Loading physician intelligence…</div>}
        {!loading && d && !d.ready && (
          <div className="mt-8 rounded-xl border border-dashed border-[#E5DCC4] p-8 bg-white">
            <div className="text-[13px] text-[#7A857F]">{d.message}</div>
          </div>
        )}

        {!loading && d?.ready && (
          <>
            <section className="mt-7 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="phys-intel-stats">
              <Stat value={fmtInt(d.physicians_indexed)} label="Physicians indexed" accent="text-[#4A7C5E]" />
              <Stat value={fmtInt(d.facility_links)} label="Likely-alignment links" />
              <Stat value={fmtInt(d.pm_r_total)} label="PM&R physicians" />
              <Stat value={fmtInt(d.internal_med_total)} label="Internal Medicine" />
              <Stat value={fmtInt(d.app_total)} label="NP / PA" />
            </section>

            {/* Top physicians */}
            <section className="mt-6 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
                <TrendingUp size={14} className="text-[#4A7C5E]"/>
                <h2 className="font-display text-base font-semibold text-[#1A2622]">Top physicians by SNF activity</h2>
                <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">By Part B services</span>
              </div>
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                  <tr>
                    <th className="text-left px-4 py-2.5 w-10">#</th>
                    <th className="text-left px-4 py-2.5">Physician</th>
                    <th className="text-left px-4 py-2.5">Specialty</th>
                    <th className="text-left px-4 py-2.5">Location</th>
                    <th className="text-right px-4 py-2.5">SNF services</th>
                    <th className="text-right px-4 py-2.5">Beneficiaries</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.top_physicians || []).map((p, i) => (
                    <tr key={p.npi} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`top-phys-${i}`}>
                      <td className="px-4 py-2.5 text-[#7A857F]">{i + 1}</td>
                      <td className="px-4 py-2.5">
                        <Link to={`/physicians/${p.npi}`} className="text-[#1A2622] font-medium hover:text-[#2D5F4F]">{p.name}</Link>
                      </td>
                      <td className="px-4 py-2.5"><div className="text-[#3A4A44] truncate max-w-[200px]" title={p.specialty}>{p.specialty || '—'}</div><div className="mt-1"><SpecBadge p={p}/></div></td>
                      <td className="px-4 py-2.5 text-[#5A6660]">{p.city}, {p.state}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmtInt(p.snf_services)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(p.snf_beneficiaries)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F0E7D2]">
                  <h2 className="font-display text-base font-semibold text-[#1A2622]">Top facilities by physician alignment</h2>
                </div>
                <ul>
                  {(d.top_facilities_aligned || []).map((f, i) => (
                    <li key={f.ccn} className="px-4 py-2.5 flex items-center justify-between gap-3 border-t border-[#F0E7D2] first:border-t-0">
                      <div className="min-w-0">
                        <Link to={`/facilities/${f.ccn}`} className="text-[12.5px] font-medium text-[#1A2622] hover:text-[#2D5F4F] truncate block">
                          {i + 1}. {f.facility_name}
                        </Link>
                        <div className="text-[10.5px] text-[#7A857F]">{f.facility_state} · {f.aligned_physicians} aligned · {fmtInt(f.total_snf_services)} svcs</div>
                      </div>
                      <span className="text-[12.5px] font-semibold text-[#4A7C5E] tabular-nums">{f.alignment_score?.toFixed(0)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
                  <AlertTriangle size={14} className="text-[#C75D5D]"/>
                  <h2 className="font-display text-base font-semibold text-[#1A2622]">Facilities with coverage gaps</h2>
                  <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">Missing PM&R / IM</span>
                </div>
                <ul>
                  {(d.facilities_with_gaps || []).map((f) => (
                    <li key={f.ccn} className="px-4 py-2.5 flex items-center justify-between gap-3 border-t border-[#F0E7D2] first:border-t-0">
                      <div className="min-w-0">
                        <Link to={`/facilities/${f.ccn}`} className="text-[12.5px] font-medium text-[#1A2622] hover:text-[#2D5F4F] truncate block">{f.facility_name}</Link>
                        <div className="text-[10.5px] text-[#7A857F]">{f.facility_state} · gaps: {f.gaps?.join(', ') || '—'}</div>
                      </div>
                      <span className="text-[12.5px] font-semibold text-[#C75D5D] tabular-nums">{fmtInt(f.total_snf_services)} svcs</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F0E7D2]"><h2 className="font-display text-base font-semibold text-[#1A2622]">Specialty distribution</h2></div>
                <ul>
                  {(d.specialty_distribution || []).map((s, i) => (
                    <li key={`${s.specialty}-${i}`} className="px-4 py-2.5 flex items-center gap-3 border-t border-[#F0E7D2] first:border-t-0">
                      <span className="flex-1 text-[12.5px] text-[#1A2622] truncate">{s.specialty || '—'}</span>
                      <span className="text-[10.5px] text-[#7A857F]">{fmtInt(s.physicians)} phys</span>
                      <span className="text-[12.5px] font-semibold tabular-nums">{fmtInt(s.services)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F0E7D2]"><h2 className="font-display text-base font-semibold text-[#1A2622]">State leaderboard</h2></div>
                <table className="w-full text-[12.5px]">
                  <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                    <tr>
                      <th className="text-left px-4 py-2.5 w-12">State</th>
                      <th className="text-right px-4 py-2.5">Physicians</th>
                      <th className="text-right px-4 py-2.5">PM&R</th>
                      <th className="text-right px-4 py-2.5">IM</th>
                      <th className="text-right px-4 py-2.5">SNF Services</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.state_leaderboard || []).map((s) => (
                      <tr key={s.state} className="border-t border-[#F0E7D2]">
                        <td className="px-4 py-2.5 font-semibold">{s.state}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(s.physicians)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-[#2D5F4F]">{fmtInt(s.pmr)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-[#A06628]">{fmtInt(s.im)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmtInt(s.services)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="mt-6 flex items-center gap-2">
              <Link to="/physicians" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#1A2622] text-[#FBF6EC] text-[13px] hover:bg-[#2D5F4F]">
                Browse all physicians <ArrowUpRight size={13}/>
              </Link>
            </div>

            <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA]">
              <Info size={12} className="mt-0.5 shrink-0"/>
              <span><strong>Source:</strong> {d.source}. <strong>Secondary:</strong> {d.secondary_source}. {d.label}.
                All alignment is INFERRED from public Part B claims volume + geographic proximity — never patient-level data.</span>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
